import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../styles/MobilityTolling.css';
import { translations } from '../translations';

const MobilityTolling = ({ language }) => {
  const t = translations[language];
  const [selectedCard, setSelectedCard] = useState(null);
  const [mounted, setMounted] = useState(false);
  const isKannada = language === 'kannada';
  const readMoreText = isKannada ? 'ಇನ್ನಷ್ಟು ಓದಿ…' : 'Read more…';

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedCard !== null ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedCard]);

  useEffect(() => {
    if (!selectedCard) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSelectedCard(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedCard]);

  const handleCloseModal = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.closest('.mobility-modal-close')) {
      setSelectedCard(null);
    }
  }, []);
  
  const features = useMemo(() => [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16.247 7.761a6 6 0 0 1 0 8.478"></path>
          <path d="M19.075 4.933a10 10 0 0 1 0 14.134"></path>
          <path d="M4.925 19.067a10 10 0 0 1 0-14.134"></path>
          <path d="M7.753 16.239a6 6 0 0 1 0-8.478"></path>
          <circle cx="12" cy="12" r="2"></circle>
        </svg>
      ),
      title: typeof t.mobility.features.etc === 'object' ? t.mobility.features.etc.heading : t.mobility.features.etc,
      description: typeof t.mobility.features.etc === 'object' ? t.mobility.features.etc.content : t.mobility.features.etcDesc,
      readMoreContent: typeof t.mobility.features.etc === 'object' ? t.mobility.features.etc.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="14" x="2" y="5" rx="2"></rect>
          <line x1="2" x2="22" y1="10" y2="10"></line>
        </svg>
      ),
      title: typeof t.mobility.features.touchGo === 'object' ? t.mobility.features.touchGo.heading : t.mobility.features.touchGo,
      description: typeof t.mobility.features.touchGo === 'object' ? t.mobility.features.touchGo.content : t.mobility.features.touchGoDesc,
      readMoreContent: typeof t.mobility.features.touchGo === 'object' ? t.mobility.features.touchGo.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path>
          <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path>
        </svg>
      ),
      title: typeof t.mobility.features.manual === 'object' ? t.mobility.features.manual.heading : t.mobility.features.manual,
      description: typeof t.mobility.features.manual === 'object' ? t.mobility.features.manual.content : t.mobility.features.manualDesc,
      readMoreContent: typeof t.mobility.features.manual === 'object' ? t.mobility.features.manual.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 17v4"></path>
          <path d="M22 12.307V15a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8.693"></path>
          <path d="M8 21h8"></path>
          <circle cx="19" cy="6" r="3"></circle>
        </svg>
      ),
      title: typeof t.mobility.features.tmc === 'object' ? t.mobility.features.tmc.heading : t.mobility.features.tmc,
      description: typeof t.mobility.features.tmc === 'object' ? t.mobility.features.tmc.content : t.mobility.features.tmcDesc,
      readMoreContent: typeof t.mobility.features.tmc === 'object' ? t.mobility.features.tmc.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 8h14"></path>
          <path d="M5 12h14"></path>
          <path d="M5 16h14"></path>
        </svg>
      ),
      title: typeof t.mobility.features.mainlinePlazas === 'object' ? t.mobility.features.mainlinePlazas.heading : t.mobility.features.mainlinePlazas,
      description: typeof t.mobility.features.mainlinePlazas === 'object' ? t.mobility.features.mainlinePlazas.content : t.mobility.features.mainlinePlazasDesc,
      readMoreContent: typeof t.mobility.features.mainlinePlazas === 'object' ? t.mobility.features.mainlinePlazas.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6"></path>
        </svg>
      ),
      title: typeof t.mobility.features.entryExitPlazas === 'object' ? t.mobility.features.entryExitPlazas.heading : t.mobility.features.entryExitPlazas,
      description: typeof t.mobility.features.entryExitPlazas === 'object' ? t.mobility.features.entryExitPlazas.content : t.mobility.features.entryExitPlazasDesc,
      readMoreContent: typeof t.mobility.features.entryExitPlazas === 'object' ? t.mobility.features.entryExitPlazas.readMoreContent : null
    }
  ], [t.mobility.features]);

  const selectedFeature = selectedCard !== null && features[selectedCard] && features[selectedCard].readMoreContent ? features[selectedCard] : null;

  return (
    <>
      <section id="mobility" className="mobility-section">
        <div className="container">
          <div className="mobility-content">
            <div className="section-header">
              <h2 className="section-title">{t.mobility.title}</h2>
              <h3 className="section-subtitle">{t.mobility.subtitle}</h3>
              <p className="section-description mobility-section-lead">{t.mobility.description}</p>
            </div>
            <div className="mobility-grid">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="mobility-card"
                  onClick={() => feature.readMoreContent && setSelectedCard(index)}
                >
                  <div className="mobility-icon">{feature.icon}</div>
                  <div>
                    <h4 className="mobility-title">{feature.title}</h4>
                    {feature.description && (
                      <p className="mobility-description">
                        {feature.description}
                        {feature.readMoreContent && (
                          <span className="mobility-read-more"> {readMoreText}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mobility-footer">
              <p 
                className="footer-text"
                dangerouslySetInnerHTML={{ __html: t.mobility.footer }}
              />
            </div>
          </div>
        </div>
      </section>

      {selectedFeature && mounted && createPortal(
        <div className="mobility-modal-overlay" onClick={handleCloseModal}>
          <div className="mobility-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="mobility-modal-close" 
              onClick={() => setSelectedCard(null)} 
              aria-label="Close"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18"></path>
                <path d="M6 6l12 12"></path>
              </svg>
            </button>
            <div className="mobility-modal-body">
              <div className="mobility-modal-icon">{features[selectedCard].icon}</div>
              <h2 className="mobility-modal-heading">{selectedFeature.title}</h2>
              <p className="mobility-modal-text">{selectedFeature.description}</p>
              <p className="mobility-modal-text">{selectedFeature.readMoreContent}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MobilityTolling;
